import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { ManualBillItem } from '@/types';
import { useI18n } from '@/hooks/useI18n';

interface ManualItemDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (description: string, amount: number) => Promise<void>;
  manualItem?: ManualBillItem | null;
  isLoading?: boolean;
}

const ManualItemDialog: React.FC<ManualItemDialogProps> = ({
  open,
  onClose,
  onConfirm,
  manualItem,
  isLoading = false,
}) => {
  const { t } = useI18n();
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (manualItem) {
        setDescription(manualItem.description);
        setAmount(manualItem.amount.toString());
      } else {
        setDescription('');
        setAmount('');
      }
      setError(null);
    }
  }, [open, manualItem]);

  const handleConfirm = async () => {
    // Validate inputs
    if (!description.trim()) {
      setError(t('bill.persistent.manualItemDescription') + ' ' + t('common.required'));
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      setError(t('bill.persistent.invalidManualItemAmount'));
      return;
    }

    try {
      await onConfirm(description.trim(), parsedAmount);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bill.persistent.billGenerationError'));
    }
  };

  const isEditMode = !!manualItem;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 3 },
          maxHeight: { xs: '90vh', sm: '80vh' },
        },
      }}
    >
      <DialogTitle>
        {isEditMode
          ? t('bill.persistent.editManualItem')
          : t('bill.persistent.addManualItem')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary">
            {t('bill.persistent.manualItemsHelp')}
          </Typography>

          <TextField
            label={t('bill.persistent.manualItemDescription')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('bill.persistent.manualItemDescriptionPlaceholder')}
            fullWidth
            required
            disabled={isLoading}
            multiline
            rows={2}
          />

          <TextField
            label={t('bill.persistent.manualItemAmount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('bill.persistent.manualItemAmountPlaceholder')}
            fullWidth
            required
            disabled={isLoading}
            type="number"
            inputProps={{
              step: '0.01',
            }}
            helperText="€"
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          onClick={onClose}
          disabled={isLoading}
          fullWidth
          sx={{ order: { xs: 2, sm: 1 } }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} />
            ) : isEditMode ? (
              <Edit />
            ) : (
              <Add />
            )
          }
          fullWidth
          sx={{ order: { xs: 1, sm: 2 } }}
        >
          {isEditMode ? t('common.update') : t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualItemDialog;
